import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Loader2, Shield, Check } from "lucide-react";
import { SpurwechselLogo } from "@/components/SpurwechselLogo";
import { toast } from "sonner";

export default function RegisterPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [inviteData, setInviteData] = useState(null);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState(null);
  const { register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const stelleCode = searchParams.get("stelle") || "";
  const inviteToken = searchParams.get("invite") || "";

  // Validate invite token on mount
  useEffect(() => {
    if (!inviteToken) return;
    setInviteLoading(true);
    api.get(`/auth/validate-invite?token=${inviteToken}`)
      .then((res) => {
        if (res.data.valid) {
          setInviteData(res.data);
          setName(res.data.name || "");
          setEmail(res.data.email || "");
        } else {
          setInviteError(res.data.reason);
        }
      })
      .catch(() => setInviteError("Einladung konnte nicht validiert werden."))
      .finally(() => setInviteLoading(false));
  }, [inviteToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Bitte geben Sie Ihren Namen ein"); return; }
    if (!email.trim() || !email.includes("@")) { toast.error("Bitte geben Sie eine gueltige E-Mail-Adresse ein"); return; }
    if (password.length < 8) { toast.error("Passwort muss mindestens 8 Zeichen lang sein"); return; }
    setLoading(true);
    try {
      const result = await register(email.trim(), password, name.trim(), inviteToken || null);
      // Auto-link to stelle if code was in URL
      if (stelleCode && !inviteToken) {
        try {
          await api.post("/expert/stelle-link", { stelle_code: stelleCode });
          toast.success("Konto erstellt und mit Beratungsstelle verbunden!");
        } catch { toast.success("Konto erstellt!"); }
      } else if (result.berater_trial) {
        toast.success("Berater-Testzugang aktiviert! Willkommen.");
      } else {
        toast.success("Konto erstellt!");
      }
      navigate("/onboarding");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === "string" && detail.includes("bereits registriert")) {
        toast.error("Diese E-Mail-Adresse ist bereits registriert. Versuchen Sie sich anzumelden.");
      } else if (detail) {
        toast.error(detail);
      } else if (err.code === "ERR_NETWORK") {
        toast.error("Verbindung zum Server fehlgeschlagen. Bitte versuchen Sie es in einigen Sekunden erneut.");
      } else {
        toast.error("Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (inviteLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (inviteError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4" data-testid="invite-error">
        <div className="text-center max-w-md">
          <SpurwechselLogo size="large" />
          <h1 className="font-heading text-2xl font-bold mt-6 mb-2">Einladung ungueltig</h1>
          <p className="font-body text-sm text-muted-foreground mb-6">{inviteError}</p>
          <Button asChild className="font-body">
            <Link to="/register">Ohne Einladung registrieren</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 page-transition" data-testid="register-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex mb-6">
            <SpurwechselLogo size="large" />
          </Link>
          {inviteData ? (
            <>
              <h1 className="font-heading text-2xl font-bold">Berater-Testzugang</h1>
              <p className="font-body text-sm text-muted-foreground mt-1">
                Kostenloser Vollzugang fuer {inviteData.duration_days} Tage
              </p>
            </>
          ) : (
            <>
              <h1 className="font-heading text-2xl font-bold">Konto erstellen</h1>
              <p className="font-body text-sm text-muted-foreground mt-1">Starten Sie Ihre professionelle MPU-Vorbereitung</p>
            </>
          )}
        </div>

        {inviteData && (
          <Card className="border-primary/30 bg-primary/5 mb-4" data-testid="invite-banner">
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="w-4 h-4 text-primary" />
                <span className="font-body text-sm font-medium">Persoenliche Einladung</span>
                <Badge variant="secondary" className="font-body text-xs ml-auto">{inviteData.duration_days} Tage</Badge>
              </div>
              <div className="space-y-1">
                {["Alle 6 Lernpfad-Stufen mit KI-Personas", "MPU-Simulationen mit verschiedenen Gutachtern", "Beratungsstellen-Dashboard mit Klienten"].map((t, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Check className="w-3 h-3 text-emerald-500 flex-shrink-0" />
                    <span className="font-body text-xs text-muted-foreground">{t}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="shadow-sm border-border/50">
          <CardContent className="pt-6 space-y-4">
            {!inviteData && (
              <>
                <Button variant="outline" className="w-full font-body gap-2 h-11" onClick={loginWithGoogle} data-testid="google-register-btn">
                  <svg className="w-5 h-5" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                  Mit Google registrieren
                </Button>
                <div className="relative">
                  <Separator />
                  <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card px-3 font-body text-xs text-muted-foreground">oder</span>
                </div>
              </>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="font-body text-sm">Name</Label>
                <Input value={name} onChange={e => setName(e.target.value)} placeholder="Ihr vollstaendiger Name" className="mt-1 h-11 font-body" required data-testid="register-name-input" />
              </div>
              <div>
                <Label className="font-body text-sm">E-Mail</Label>
                <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="ihre@email.de" className="mt-1 h-11 font-body" required data-testid="register-email-input" readOnly={!!inviteData} />
              </div>
              <div>
                <Label className="font-body text-sm">Passwort</Label>
                <Input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Mindestens 8 Zeichen" className="mt-1 h-11 font-body" required data-testid="register-password-input" />
              </div>
              <Button type="submit" className="w-full h-11 font-body bg-primary text-primary-foreground hover:bg-primary/90" disabled={loading} data-testid="register-submit-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {inviteData ? "Testzugang aktivieren" : "Registrieren"}
              </Button>
            </form>
            <p className="font-body text-xs text-muted-foreground text-center pt-2">
              Mit der Registrierung stimmen Sie unseren <Link to="/agb" className="text-primary hover:underline">AGB</Link> und <Link to="/privacy" className="text-primary hover:underline">Datenschutzrichtlinien</Link> zu.
            </p>
          </CardContent>
        </Card>
        <p className="text-center font-body text-sm text-muted-foreground mt-4">
          Bereits registriert? <Link to="/login" className="text-primary hover:underline font-medium" data-testid="go-to-login">Anmelden</Link>
        </p>
      </div>
    </div>
  );
}
